'use client';

import { useState, useEffect, useRef } from 'react';
import styles from './styles.module.css';
import { useContent } from '../../content/current';

const INITIAL_FORM = {
  nombre: '',
  empresa: '',
  email: '',
  telefono: '',
  mensaje: '',
  privacidad: false,
};

export default function ContactPanel({ isOpen, onClose }) {
  const content = useContent();
  const contact = content.contact;
  const [form, setForm] = useState(INITIAL_FORM);
  const [status, setStatus] = useState(null);
  const innerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e) => {
      if (innerRef.current && !innerRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, onClose]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setStatus('sent');
        setForm(INITIAL_FORM);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div
      className={`${styles.overlay}${isOpen ? ` ${styles.overlayOpen}` : ''}`}
      aria-hidden={!isOpen}
    >
      <div className={styles.panel} role="dialog" aria-modal="true" aria-label={contact.title}>
        <div className={styles.inner} ref={innerRef}>
          <button className={styles.close} type="button" onClick={onClose} aria-label={contact.close}>
            ✕
          </button>
          <h2 className={styles.title}>{contact.title}</h2>

          {status === 'sent' ? (
            <p className={styles.success}>{contact.success}</p>
          ) : (
            <form className={styles.form} onSubmit={handleSubmit}>
              <div className={styles.field}>
                <label className={styles.label} htmlFor="contact-nombre">{contact.fields.name}</label>
                <input
                  className={styles.input}
                  id="contact-nombre"
                  name="nombre"
                  type="text"
                  value={form.nombre}
                  onChange={handleChange}
                  required
                  autoComplete="name"
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="contact-empresa">{contact.fields.company}</label>
                <input
                  className={styles.input}
                  id="contact-empresa"
                  name="empresa"
                  type="text"
                  value={form.empresa}
                  onChange={handleChange}
                  autoComplete="organization"
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="contact-email">{contact.fields.email}</label>
                <input
                  className={styles.input}
                  id="contact-email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  autoComplete="email"
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="contact-telefono">{contact.fields.phone}</label>
                <input
                  className={styles.input}
                  id="contact-telefono"
                  name="telefono"
                  type="tel"
                  value={form.telefono}
                  onChange={handleChange}
                  autoComplete="tel"
                />
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="contact-mensaje">{contact.fields.message}</label>
                <textarea
                  className={styles.textarea}
                  id="contact-mensaje"
                  name="mensaje"
                  value={form.mensaje}
                  onChange={handleChange}
                  required
                  rows={4}
                />
              </div>

              <div className={styles.fieldCheckbox}>
                <input
                  className={styles.checkbox}
                  id="contact-privacidad"
                  name="privacidad"
                  type="checkbox"
                  checked={form.privacidad}
                  onChange={handleChange}
                  required
                />
                <label className={styles.labelCheckbox} htmlFor="contact-privacidad">
                  {contact.privacyPrefix}{' '}
                  <a href="/politica-de-privacidad" target="_blank" rel="noopener noreferrer" className={styles.privacidadLink}>
                    {contact.privacyLink}
                  </a>
                </label>
              </div>

              {status === 'error' && (
                <p className={styles.error} role="alert">
                  {contact.error}
                </p>
              )}

              <button className={styles.submit} type="submit" disabled={status === 'sending'}>
                {status === 'sending' ? contact.sending : contact.submit}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
